import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { UnizenLogo } from "@/components/unizen-logo";
import { useNotifications } from "@/features/notifications/hooks/use-notifications";
import { useSwipeNavigation } from "@/hooks/use-swipe-navigation";
import { useSession } from "@/lib/auth-client";
import { cn } from "@/lib/utils";
import { Link, useRouterState } from "@tanstack/react-router";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import {
  BarChart3,
  Bell,
  BookOpen,
  Calendar,
  ChevronRight,
  Clock3,
  GraduationCap,
  Home,
  Settings,
  Users,
} from "lucide-react";
import { type ReactNode, useRef, useState } from "react";
import { createPortal } from "react-dom";

export const MOBILE_NAV_ROUTES = ["/", "/timetable", "/attendance"] as const;

const mobileBottomNavItems = [
  { to: MOBILE_NAV_ROUTES[0], icon: Home, label: "Home" },
  { to: MOBILE_NAV_ROUTES[1], icon: Calendar, label: "Schedule" },
  { to: MOBILE_NAV_ROUTES[2], icon: GraduationCap, label: "Attendance" },
] as const;

const desktopSidebarItems = [
  { to: "/", icon: Home, label: "Home" },
  { to: "/timetable", icon: Calendar, label: "Schedule" },
  { to: "/attendance", icon: GraduationCap, label: "Attendance" },
  { to: "/results", icon: BarChart3, label: "Results" },
  { to: "/courses", icon: BookOpen, label: "Courses" },
  { to: "/reminders", icon: Clock3, label: "Reminders" },
  { to: "/common-breaks", icon: Users, label: "Common Breaks" },
] as const;

const menuShortcutItems = [
  { to: "/results", icon: BarChart3, label: "Results" },
  { to: "/courses", icon: BookOpen, label: "Courses" },
  { to: "/myexam", icon: BookOpen, label: "Exams" },
  { to: "/reminders", icon: Clock3, label: "Reminders" },
  { to: "/common-breaks", icon: Users, label: "Common Breaks" },
  { to: "/notifications", icon: Bell, label: "Notifications" },
  { to: "/settings", icon: Settings, label: "Settings" },
] as const;

const desktopSecondaryItems = [
  { to: "/notifications", icon: Bell, label: "Notifications" },
  { to: "/settings", icon: Settings, label: "Settings" },
] as const;

interface AppLayoutProps {
  children: ReactNode;
}

function isActiveRoute(currentPath: string, targetPath: string): boolean {
  if (targetPath === "/") {
    return currentPath === "/";
  }

  return currentPath === targetPath || currentPath.startsWith(`${targetPath}/`);
}

export function AppLayout({ children }: AppLayoutProps) {
  const swipeRef = useRef<HTMLDivElement>(null);
  useSwipeNavigation(swipeRef);
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });
  const shouldReduceMotion = useReducedMotion();

  return (
    <TooltipProvider>
      <div className="flex h-screen overflow-x-hidden bg-background">
        {/* Desktop sidebar */}
        <DesktopSidebar />

        {/* Main content */}
        <main className="min-w-0 flex-1 overflow-x-hidden overflow-y-auto pb-16 lg:pb-0">
          {/* Mobile header */}
          <MobileHeader />

          <div
            ref={swipeRef}
            className="mx-auto min-w-0 w-full max-w-6xl px-4 py-6 sm:px-6 lg:px-8 [touch-action:pan-y]"
          >
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentPath}
                initial={
                  shouldReduceMotion
                    ? { opacity: 1 }
                    : { opacity: 0, y: 6, scale: 0.995 }
                }
                animate={
                  shouldReduceMotion
                    ? { opacity: 1 }
                    : { opacity: 1, y: 0, scale: 1 }
                }
                exit={
                  shouldReduceMotion
                    ? { opacity: 1 }
                    : { opacity: 0, y: -4, scale: 0.995 }
                }
                transition={{
                  duration: shouldReduceMotion ? 0.12 : 0.18,
                  ease: "easeOut",
                }}
              >
                {children}
              </motion.div>
            </AnimatePresence>
          </div>
        </main>

        {/* Mobile bottom nav */}
        <MobileBottomNav />
      </div>
    </TooltipProvider>
  );
}

function DesktopSidebar() {
  const { data: session } = useSession();
  const { data: notifications } = useNotifications();
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });
  const hasUnread = (notifications?.unreadCount ?? 0) > 0;

  return (
    <aside className="hidden h-screen w-64 flex-shrink-0 flex-col overflow-y-auto border-r border-sidebar-border bg-sidebar lg:flex">
      <div className="flex h-14 shrink-0 items-center justify-between gap-3 px-4">
        <Link to="/" className="flex min-w-0 items-center gap-2">
          <UnizenLogo className="h-8 w-8 rounded-full object-cover text-primary" />
        </Link>
        <AppCornerMenu />
      </div>

      <Separator className="bg-sidebar-border" />

      {/* Primary nav */}
      <nav className="flex-1 space-y-1 px-3 py-4 overflow-y-auto custom-scrollbar">
        {desktopSidebarItems.map((item) => (
          <Tooltip key={item.to}>
            <TooltipTrigger asChild>
              <Link
                to={item.to}
                className={cn(
                  "group relative flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-sm font-medium transition-all duration-200",
                  isActiveRoute(currentPath, item.to)
                    ? "border-sidebar-primary/20 bg-sidebar-primary/10 text-sidebar-primary"
                    : "text-sidebar-foreground/70 hover:border-sidebar-primary/15 hover:bg-sidebar-primary/10 hover:text-sidebar-primary",
                )}
              >
                <item.icon className="h-5 w-5 transition-transform duration-200 group-hover:scale-105" />
                {item.label}
              </Link>
            </TooltipTrigger>
            <TooltipContent side="right">{item.label}</TooltipContent>
          </Tooltip>
        ))}
      </nav>

      {/* Secondary nav & User profile inside corner/bottom menu area */}
      <div className="shrink-0 space-y-1 px-3 pb-4 pt-2">
        <Separator className="mb-4 bg-sidebar-border" />
        {desktopSecondaryItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              "group relative flex items-center gap-3 rounded-lg border border-transparent px-3 py-2 text-sm font-medium transition-all duration-200",
              isActiveRoute(currentPath, item.to)
                ? "border-sidebar-primary/20 bg-sidebar-primary/10 text-sidebar-primary"
                : "text-sidebar-foreground/70 hover:border-sidebar-primary/15 hover:bg-sidebar-primary/10 hover:text-sidebar-primary",
            )}
          >
            <span className="relative">
              <item.icon className="h-5 w-5 transition-transform duration-200 group-hover:scale-105" />
              {item.to === "/notifications" && hasUnread && (
                <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-destructive animate-pulse" />
              )}
            </span>
            {item.label}
          </Link>
        ))}
      </div>

      {/* User */}
      {session?.user && (
        <div className="shrink-0 border-t border-sidebar-border px-3 py-3">
          <div className="flex items-center gap-3 rounded-lg px-3 py-2">
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-sidebar-accent text-xs text-sidebar-accent-foreground">
                {session.user.name
                  ?.split(" ")
                  .map((n) => n[0])
                  .join("")
                  .toUpperCase()
                  .slice(0, 2) ?? "U"}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 overflow-hidden">
              <p className="truncate text-sm font-medium text-sidebar-foreground">
                {session.user.name}
              </p>
              <p className="truncate text-xs text-sidebar-foreground/60">
                {session.user.email}
              </p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}

function MobileHeader() {
  const { data: notifications } = useNotifications();
  const hasUnread = (notifications?.unreadCount ?? 0) > 0;

  return (
    <header className="sticky top-0 z-[var(--z-sticky)] flex h-14 items-center justify-between border-b border-border/80 bg-background/95 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/70 lg:hidden">
      <Link to="/" className="flex items-center gap-2">
        <UnizenLogo className="h-7 w-7 rounded-full object-cover text-primary" />
        <span className="text-sm font-black tracking-tight text-foreground">
          UNIZEN
        </span>
      </Link>

      <div className="flex items-center gap-2">
        <Link to="/notifications">
          <Button
            variant="ghost"
            size="icon"
            aria-label="Notifications"
            className="relative border border-transparent text-muted-foreground transition-colors hover:border-primary/20 hover:bg-primary/10 hover:text-primary"
          >
            <Bell className="h-5 w-5" />
            {hasUnread && (
              <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-destructive animate-pulse" />
            )}
          </Button>
        </Link>
        <AppCornerMenu />
      </div>
    </header>
  );
}

function AppCornerMenu() {
  const [open, setOpen] = useState(false);
  const { data: session } = useSession();
  const { data: notifications } = useNotifications();
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });
  const hasUnread = (notifications?.unreadCount ?? 0) > 0;

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Open profile menu"
          className="relative h-8 w-8 rounded-full border border-transparent text-muted-foreground transition-colors hover:border-primary/20 hover:bg-primary/10 hover:text-primary"
        >
          <Avatar className="h-8 w-8 bg-muted/50 border border-primary/20 hover:border-primary/50 transition-colors">
            <AvatarFallback className="bg-transparent text-xs font-semibold text-primary">
              {session?.user?.name ? (
                session.user.name.charAt(0).toUpperCase()
              ) : (
                <Users className="h-4 w-4" />
              )}
            </AvatarFallback>
          </Avatar>
          {hasUnread && (
            <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-destructive animate-pulse" />
          )}
        </Button>
      </SheetTrigger>

      <SheetContent side="right" className="w-[85vw] p-0 sm:w-80">
        <SheetHeader className="space-y-4 border-b border-border/70 p-6">
          <div className="pr-8">
            <SheetTitle>{session?.user?.name || "Profile"}</SheetTitle>
            <SheetDescription>
              {session?.user?.email || "Account & Settings"}
            </SheetDescription>
          </div>

          {session?.user && (
            <div className="flex items-center gap-3 rounded-lg border border-border bg-card p-3 text-left">
              <Avatar className="h-10 w-10">
                <AvatarFallback className="bg-primary/10 text-sm text-primary">
                  {session.user.name
                    ?.split(" ")
                    .map((n) => n[0])
                    .join("")
                    .toUpperCase()
                    .slice(0, 2) ?? "U"}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-foreground">
                  {session.user.name}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {session.user.email}
                </p>
              </div>
            </div>
          )}
        </SheetHeader>

        <div className="flex-1 space-y-2 overflow-y-auto p-4">
          {menuShortcutItems.map((item) => (
            <SheetClose key={item.to} asChild>
              <Link
                to={item.to}
                className={cn(
                  "group flex items-center gap-3 rounded-lg border border-transparent px-3 py-3 text-sm font-medium transition-all duration-200",
                  isActiveRoute(currentPath, item.to)
                    ? "border-primary/20 bg-primary/10 text-primary"
                    : "text-foreground hover:border-primary/15 hover:bg-accent hover:text-primary",
                )}
              >
                <span className="relative flex h-9 w-9 items-center justify-center rounded-lg bg-muted text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary">
                  <item.icon className="h-4 w-4" />
                  {item.to === "/notifications" && hasUnread && (
                    <span className="absolute top-1.5 right-1.5 h-2 w-2 rounded-full bg-destructive animate-pulse" />
                  )}
                </span>
                <span className="flex-1">{item.label}</span>
                <ChevronRight className="h-4 w-4 text-muted-foreground transition-colors group-hover:text-primary" />
              </Link>
            </SheetClose>
          ))}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function MobileBottomNav() {
  const currentPath = useRouterState({
    select: (state) => state.location.pathname,
  });

  return createPortal(
    <nav
      className="fixed inset-x-0 bottom-0 z-[var(--z-fixed)] border-t border-border/80 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 lg:hidden"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="flex h-16 items-center justify-around">
        {mobileBottomNavItems.map((item) => (
          <Link
            key={item.to}
            to={item.to}
            className={cn(
              "relative flex flex-col items-center justify-center gap-0.5 rounded-lg border border-transparent px-3 py-1.5 text-xs transition-all duration-200",
              isActiveRoute(currentPath, item.to)
                ? "border-primary/20 bg-primary/10 text-primary"
                : "text-muted-foreground hover:border-primary/15 hover:bg-primary/10 hover:text-primary",
            )}
            aria-label={item.label}
          >
            <item.icon className="h-5 w-5" />
            <span className="text-xs leading-none">{item.label}</span>
            {isActiveRoute(currentPath, item.to) && (
              <span className="absolute -bottom-1.5 left-1/2 h-0.5 w-5 -translate-x-1/2 rounded-full bg-primary/70 transition-all duration-300" />
            )}
          </Link>
        ))}
      </div>
    </nav>,
    document.body,
  );
}
